import { IUser, UserPresence, UserPresenceMap } from './types/User'
import { Message } from './firebase'
import { identify } from './posthog'
import { loadUserFromLocalStorage } from './users'
import { PlacableItem } from './types/Whiteboard'
import React, { useReducer, useContext, ReactNode, useEffect } from 'react'
import localStore from 'store'
import omit from 'lodash/omit'
import { differenceBy } from 'lodash'

export type ToolType = 'select' | 'drawing' | 'text' | 'note' | 'video-frame'
export type DrawingToolType = 'highlight' | 'rectangle' | 'ellipse' | 'line' | 'pen'

export interface State {
  user: IUser
  isAuthed: boolean
  userSynced: boolean
  users: UserPresenceMap

  userList: UserPresence[]
  userMicrophonesChangedTs: number // value to reactively track changes

  usersWaiting: UserPresenceMap
  bots: UserPresenceMap

  hasAcceptedStage: boolean
  isAgendaOpened: boolean

  tool: ToolType
  drawingTool: DrawingToolType
  sideMenu: boolean | null
  color: string

  noteVal?: string
  createActivity: boolean
  audioOutputDevice: string
  audioInputDevice: string | null
  videoInputDevice: string | null
  visibleScreenShare?: string[]
  visibleVideos?: string[]
  meetingConfig?: any
  breakoutRoomId?: string
  sessionId?: string
  showOutcome?: boolean
  inMeeting?: boolean
  // onlineStatus: 'connected' | 'disconnected'
  editWorkshop?: boolean

  lastMessage: Message | null

  AttendeeId: string | null
  audioVideoSet: boolean
  videoInputError?: boolean
  audioInputError?: boolean
  audioOutputError?: boolean

  transcription?: boolean
  stackOfNotes?: string[]
  placementItems?: PlacableItem[]

  personId?: string // for dragging users on board
}

const initialState: State = {
  user: {
    name: 'Guest',
    isBot: false,
    id: '',
    color: '#fff',
    presence: 'offline',
    authProvider: 'none',
  },
  isAuthed: false,
  // user: {
  //   ...(localStore.get('user') || { name: 'guest' }),
  //   loading: true,
  //   presence: 'lobby',
  // } as IUser,

  usersWaiting: {},
  bots: {},

  userSynced: false,
  users: {},
  userList: [],
  userMicrophonesChangedTs: 0,
  hasAcceptedStage: false,
  tool: 'select',
  drawingTool: 'pen',
  sideMenu: null,
  color: '#2B2626',
  // workshop: WORKSHOP_PLACEHOLDER,
  noteVal: '',
  isAgendaOpened: false,
  // isFacilitator: false,
  createActivity: false,
  AttendeeId: null,
  audioOutputDevice: localStore.get('audioOutputDevice'),
  audioInputDevice: localStore.get('audioInputDevice'),
  videoInputDevice: localStore.get('videoInputDevice'),
  // onlineStatus: 'disconnected',
  editWorkshop: false,
  lastMessage: null,
  audioVideoSet: false,
}

const ActionType = {
  UPDATE: 'UPDATE',
  AUTH_USER: 'AUTH_USER',
  CLEAR_USER: 'CLEAR_USER',
  UPDATE_USERS: 'UPDATE_USERS',
  UPDATE_USER: 'UPDATE_USER',
  SET_BREAKOUT_ROOM_ID: 'SET_BREAKOUT_ROOM_ID',
  CREATE_MEETING: 'CREATE_MEETING',
  VIDEO_ENABLED: 'VIDEO_ENABLED',
  VIDEO_DISABLED: 'VIDEO_DISABLED',
  SCREEN_SHARE_ENABLED: 'SCREEN_SHARE_ENABLED',
  RESET_VISIBLE: 'RESET_VISIBLE',
  PRESENCE: 'PRESENCE',
  PLACE_STACK_OF_NOTES: 'PLACE_STACK_OF_NOTES',
  PLACE_ITEM: 'PLACE_ITEM',
  INIT_RECORDING: 'INIT_RECORDING',
}

interface Action extends Partial<Omit<State, 'user'>> {
  type?: keyof typeof ActionType
  id?: string
  enabled?: boolean
  presence?: IUser['presence']
  user?: Partial<IUser>
}

const reducer = (state: State, action: Action): State => {
  const { type, ...payload } = action

  if (payload.audioOutputDevice) {
    localStore.set('audioOutputDevice', payload.audioOutputDevice)
  }
  if (payload.audioInputDevice) {
    localStore.set('audioInputDevice', payload.audioInputDevice)
  }
  if (payload.videoInputDevice) {
    localStore.set('videoInputDevice', payload.videoInputDevice)
  }

  switch (type) {
    case undefined:
    case ActionType.UPDATE: {
      return { ...state, ...payload } as State
    }
    case 'AUTH_USER': {
      if (!payload.user) {
        console.error('no user provided to update')
        return state
      }
      return { ...state, user: payload.user as IUser, isAuthed: true }
    }
    case 'CLEAR_USER': {
      localStore.remove('user')
      return { ...state, isAuthed: false }
    }
    case 'UPDATE_USERS': {
      const { users } = payload
      if (!users) return state
      return {
        ...state,
        users,
        userList: Object.values(users),
        userMicrophonesChangedTs:
          differenceBy(state.userList, Object.values(users), 'micOn').length > 0
            ? Date.now()
            : state.userMicrophonesChangedTs,
      }
    }
    case 'UPDATE_USER': {
      if (!payload.user) {
        console.warn('no user provided to update')
        return state
      }
      const user = { ...state.user, ...payload.user }
      localStore.set('user', omit(user, ['presence', 'loading']))
      return { ...state, user } as State
    }
    case 'SET_BREAKOUT_ROOM_ID': {
      const breakoutRoomId = payload.breakoutRoomId
      return { ...state, breakoutRoomId }
    }
    case 'VIDEO_ENABLED': {
      const { id, enabled } = payload
      const videos = state.visibleVideos || []
      if (!id) return state
      const visibleVideos = enabled ? videos.concat(id) : videos.filter(v => v !== id)
      return { ...state, visibleVideos }
    }
    case 'VIDEO_DISABLED': {
      const { id } = payload
      const visibleVideos = (state.visibleVideos || []).filter(v => v !== id)
      return { ...state, visibleVideos }
    }
    case 'SCREEN_SHARE_ENABLED': {
      const { id, enabled } = payload
      const videos = state.visibleScreenShare || []
      if (!id) return state
      const visibleScreenShare = enabled ? videos.concat(id) : videos.filter(v => v !== id)
      return { ...state, visibleScreenShare }
    }
    case 'RESET_VISIBLE': {
      return { ...state, visibleVideos: [], visibleScreenShare: [] }
    }
    case 'PLACE_STACK_OF_NOTES': {
      const { stackOfNotes } = payload
      return { ...state, stackOfNotes }
    }
    case 'PLACE_ITEM': {
      const { placementItems } = payload
      return { ...state, placementItems }
    }
    case 'INIT_RECORDING': {
      // const workshop = {
      //   ...state.workshop,
      //   recording: state.workshop.recording || {},
      // } as IWorkshop
      // workshop.recording.taskId = workshop.recording.taskId || 'starting'
      // return { ...state, workshop }
      return state
    }
    default:
      return state
  }
}
export type Dispatch = React.Dispatch<Action>
export type StoreContextType = [State, Dispatch]

const StoreContext = React.createContext<StoreContextType>([initialState, () => initialState])

const StoreProvider = ({ children }: { children: ReactNode }) => {
  const [store, dispatch] = useReducer(reducer, initialState)
  // @ts-ignore
  window.STORE = store
  // @ts-ignore
  window.DISPATCH = dispatch

  const { user } = store
  useEffect(() => {
    if (user.id) identify(user.id, { name: user?.name, email: user?.email })
  }, [user.id, user.email, user.name])

  useEffect(() => {
    const localUser = loadUserFromLocalStorage()
    if (localUser) dispatch({ type: 'AUTH_USER', user: localUser })
  }, [])

  return <StoreContext.Provider value={[store, dispatch]}>{children}</StoreContext.Provider>
}

const noopDispatch: Dispatch = () => {}

const useStore = () => {
  return useContext(StoreContext) || [initialState, noopDispatch] // fallback for HMR
}

export { StoreProvider, useStore }
